import React, { createContext, useContext, useState, useEffect, useCallback } from "react";

const ThemeContext = createContext(null);

function initialTheme() {
  try {
    const saved = localStorage.getItem("de_theme");
    if (saved === "dark" || saved === "light") return saved;
    // First visit (no explicit choice yet): follow the visitor's OS preference.
    if (window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches) return "dark";
  } catch (e) { /* ignore */ }
  return "light";
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(initialTheme);

  // Reflect the theme on <html> + meta. We do NOT persist here — so a first-time visitor
  // keeps tracking their OS setting until they explicitly pick a theme via the toggle.
  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute("content", theme === "dark" ? "#00FF41" : "#FFFFFF");
  }, [theme]);

  const persist = (t) => { try { localStorage.setItem("de_theme", t); } catch (e) { /* ignore */ } };
  const setTheme = useCallback((t) => { const v = t === "dark" ? "dark" : "light"; persist(v); setThemeState(v); }, []);
  const toggleTheme = useCallback(() => setThemeState((p) => { const v = p === "dark" ? "light" : "dark"; persist(v); return v; }), []);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) return { theme: "light", setTheme: () => {}, toggleTheme: () => {} };
  return ctx;
}
